
import React, { useState, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, CircularProgress, IconButton, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CopyExcelDataButton from './CopyExcelDataButton';
import CopyExcelDataButtonTOB from './CopyExcelDataButtonTOB';

const TableOfBenefitsDialog = ({ open, onClose, tableOfBenefits, isLoading=false }) => {
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const tableRef = useRef(null);

  const selectionTempRef = useRef({ start: null, end: null });

  const [showSnackbar, setShowSnackbar] = useState(false);



  // Function to blend original background color with blue highlight
  const blendWithHighlight = (originalColor) => {
    if (!originalColor) {
      return 'rgba(25, 118, 210, 0.1)'; // Default blue if no original color
    }

    // Parse the original color
    let r, g, b, a = 1;

    if (originalColor.startsWith('#')) {
      // Handle hex colors
      const hex = originalColor.slice(1);
      if (hex.length === 6) {
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
      } else if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      }
    } else if (originalColor.startsWith('rgb')) {
      // Handle rgb/rgba colors
      const match = originalColor.match(/rgba?\(([^)]+)\)/);
      if (match) {
        const values = match[1].split(',').map(v => parseFloat(v.trim()));
        r = values[0];
        g = values[1];
        b = values[2];
        a = values[3] || 1;
      }
    } else {
      // Handle named colors - fallback to default highlight
      return 'rgba(25, 118, 210, 0.1)';
    }

    // Blend with blue highlight (25, 118, 210) with 0.3 opacity
    const highlightR = 25;
    const highlightG = 118;
    const highlightB = 210;
    const highlightOpacity = 0.3;

    // Alpha blending formula
    const blendedR = Math.round(r * (1 - highlightOpacity) + highlightR * highlightOpacity);
    const blendedG = Math.round(g * (1 - highlightOpacity) + highlightG * highlightOpacity);
    const blendedB = Math.round(b * (1 - highlightOpacity) + highlightB * highlightOpacity);

    return `rgba(${blendedR}, ${blendedG}, ${blendedB}, ${a})`;
  };

  const handleMouseDown = (rowIdx, colIdx) => {
    // Start selection
    const cellPosition = { row: rowIdx, col: colIdx };
    setSelectionStart(cellPosition);
    setSelectionEnd(cellPosition);
    setIsSelecting(true);
  };

  const handleColumnHeaderClick = (colIdx) => {
    // Select entire column
    if (!tableOfBenefits) return;

    const numRows = Object.keys(tableOfBenefits).length;
    setSelectionStart({ row: 0, col: colIdx });
    setSelectionEnd({ row: numRows - 1, col: colIdx });
    setIsSelecting(false);
  };

  const handleRowHeaderClick = (rowIdx) => {
    // Select entire row
    if (!tableOfBenefits) return;

    const firstRow = tableOfBenefits[Object.keys(tableOfBenefits)[0]];
    const numCols = Object.keys(firstRow).length;
    setSelectionStart({ row: rowIdx, col: 0 });
    setSelectionEnd({ row: rowIdx, col: numCols - 1 });
    setIsSelecting(false);
  };

  const handleMouseOver = (rowIdx, colIdx) => {
    // Update selection range when mouse moves while button is pressed
    if (isSelecting) {
      setSelectionEnd({ row: rowIdx, col: colIdx });
    }
  };

  const handleMouseUp = () => {
    // End selection
    setIsSelecting(false);
  };

  // Determine if a cell is within the selected range
  const isCellSelected = (rowIdx, colIdx) => {
    if (!selectionStart || !selectionEnd) return false;

    const minRow = Math.min(selectionStart.row, selectionEnd.row);
    const maxRow = Math.max(selectionStart.row, selectionEnd.row);
    const minCol = Math.min(selectionStart.col, selectionEnd.col);
    const maxCol = Math.max(selectionStart.col, selectionEnd.col);

    return rowIdx >= minRow && rowIdx <= maxRow && colIdx >= minCol && colIdx <= maxCol;
  };

  // Function to copy selected data to clipboard
  const copySelectedDataToClipboard = async () => {
    if (!selectionStart || !selectionEnd || !tableOfBenefits) return;

    await handleOnCopyStart();

    var node = document.getElementById('table-of-benefits');

    node = node.cloneNode(true);

    // remove the first row and first columns
    const firstRow = node.getElementsByTagName("tr")[0];
    if (firstRow) {
      firstRow.parentNode.removeChild(firstRow);
    }


    // remove the first columns
    let rows = node.getElementsByTagName("tr");
    for (let i = 0; i < rows.length; i++) {
      const cells = rows[i].getElementsByTagName("td");
      if (cells.length > 0) {
        cells[0].parentNode.removeChild(cells[0]);
      }
    }

    rows = node.getElementsByTagName("tr");

    const cellsToBeRemoved = [];
    const rowsToBeRemoved = [];

    for (let i = 0; i < rows.length; i++) {
      const cells = rows[i].getElementsByTagName("td");
      let hasSelectedCells = false;
      for (let j = 0; j < cells.length; j++) {
        const isSelected = isCellSelected(i, j);
        if (!isSelected) {
          cellsToBeRemoved.push(cells[j]);
        } else {
          hasSelectedCells = true;
        }
      }

      if (!hasSelectedCells) {
        rowsToBeRemoved.push(rows[i]);
      }
    }

    cellsToBeRemoved.forEach(cell => cell.remove());
    rowsToBeRemoved.forEach(row => row.remove());

    const blob = new Blob([node.outerHTML], { type: "text/html" });
    const clipboardItem = new ClipboardItem({ "text/html": blob });

    await navigator.clipboard.write([clipboardItem]);

    setShowSnackbar(true);

    await handleOnCopyEnd();
  };

  // Handle document-wide mouse up to stop selection even if mouse is released outside the table
  React.useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isSelecting) {
        setIsSelecting(false);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isSelecting]);

  // Handle Ctrl+C keyboard event
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'c' && open) {
        event.preventDefault();
        copySelectedDataToClipboard();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, selectionStart, selectionEnd, tableOfBenefits]);

  async function handleOnCopyStart() {
    const tempStart = selectionStart;
    const tempEnd = selectionEnd;

    setSelectionStart(null);
    setSelectionEnd(null);

    await new Promise(resolve => setTimeout(resolve, 200));

    // Store in ref for use in onClickEnd
    selectionTempRef.current = { start: tempStart, end: tempEnd };
  }

  async function handleOnCopyEnd() {
    setSelectionStart(selectionTempRef.current.start);
    setSelectionEnd(selectionTempRef.current.end);
    selectionTempRef.current = { start: null, end: null };
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Table of Benefits
        <span>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </span>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <CopyExcelDataButtonTOB
            elementId='table-of-benefits'
            onClickStart={handleOnCopyStart}
            onClickEnd={handleOnCopyEnd}
          />
        </Box>

        {
          isLoading ?
            <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress size={32} /></Box> :
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <table
                id='table-of-benefits'
                ref={tableRef}
                style={{
                  borderSpacing: 0,
                  borderCollapse: 'collapse',
                  minWidth: '600px',
                  userSelect: 'none', // Prevent text selection during cell selection
                  width: tableOfBenefits ? `calc(470px + 250px * ${Object.keys(tableOfBenefits[Object.keys(tableOfBenefits)[0]]).length})` : '100%'
                }}
                onMouseUp={handleMouseUp}
              >
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #000000', backgroundColor: '#f0f0f0', width: '40px', textAlign: 'center' }}></th>
                    {
                      tableOfBenefits && Object.keys(tableOfBenefits[Object.keys(tableOfBenefits)[0]]).map((colKey, idx) => (
                        <th
                          key={colKey}
                          style={{
                            border: '1px solid #000000',
                            backgroundColor: '#f0f0f0',
                            textAlign: 'center',
                            width: colKey === '1' ? '470px' : '250px',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={() => handleColumnHeaderClick(idx)}
                        >
                          {String.fromCharCode(65 + idx)}
                        </th>
                      ))
                    }
                  </tr>
                </thead>
                <tbody>
                  {
                    tableOfBenefits && Object.keys(tableOfBenefits).map((rowKey, rowIdx) => {
                      const row = tableOfBenefits[rowKey];
                      return (
                        <tr key={rowKey}>
                          <td
                            style={{
                              border: '1px solid #000000',
                              backgroundColor: '#f0f0f0',
                              textAlign: 'center',
                              fontWeight: 'bold',
                              width: '40px',
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                            onClick={() => handleRowHeaderClick(rowIdx)}
                          >
                            {rowIdx + 1}
                          </td>
                          {
                            Object.keys(row).map((colKey, colIdx) => {
                              const cell = row[colKey];
                              const isSelected = isCellSelected(rowIdx, colIdx);

                              return (
                                <td
                                  key={colKey}
                                  title={cell.cell_value}
                                  onMouseDown={() => handleMouseDown(rowIdx, colIdx)}
                                  onMouseOver={() => handleMouseOver(rowIdx, colIdx)}
                                  style={{
                                    padding: 0,
                                    margin: 0,
                                    border: isSelected ? '1px solid #1976d2' : '1px solid #000000',
                                    backgroundColor: isSelected ? blendWithHighlight(cell.cell_bg_color) : cell.cell_bg_color,
                                    color: cell.cell_text_color,
                                    textAlign: cell.horizontal_alignment,
                                    fontWeight: cell.font_weight,
                                    height: '30px',
                                    width: colKey === '1' ? '470px' : '250px',
                                    textOverflow: 'ellipsis',
                                    textWrap: 'nowrap',
                                    paddingLeft: '5px',
                                    paddingRight: '5px',
                                    cursor: 'cell',
                                    position: 'relative',
                                    boxSizing: 'border-box',
                                    overflow: 'hidden'
                                  }}>
                                  {cell.cell_value}
                                </td>
                              );
                            })
                          }
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </div>
        }

        {/* Selection information display */}
        {/* {selectionStart && selectionEnd && (
          <div style={{ marginTop: '10px', fontSize: '14px', color: '#555' }}>
            Selected: {String.fromCharCode(65 + Math.min(selectionStart.col, selectionEnd.col))}{Math.min(selectionStart.row, selectionEnd.row) + 1}
            {(selectionStart.row !== selectionEnd.row || selectionStart.col !== selectionEnd.col) ?
              `:${String.fromCharCode(65 + Math.max(selectionStart.col, selectionEnd.col))}{Math.max(selectionStart.row, selectionEnd.row) + 1}` : ''}
          </div>
        )} */}
      </DialogContent>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showSnackbar}
        autoHideDuration={2500}
        onClose={() => setShowSnackbar(false)}
        message="Excel Data Copied to Clipboard. You can paste it into Excel, Google Sheets, or Outlook."
      />
    </Dialog>
  );
};

export default TableOfBenefitsDialog;
